<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>爱豆套餐</h1>
        <p>用户在「获取爱豆」页能买到的档位，价格与微信道具都在这里维护</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增套餐
        </el-button>
      </div>
    </div>

    <!-- ── 配置进度：这一页最该被看见的东西 ──────────────── -->
    <div class="pkg-progress" :class="{ 'is-done': pendingCount === 0 }">
      <div class="pkg-progress__main">
        <div class="pkg-progress__head">
          <i :class="pendingCount === 0 ? 'el-icon-circle-check' : 'el-icon-warning-outline'" />
          <span v-if="pendingCount > 0">
            {{ soldCount }} 个在售档位中，还有 <b>{{ pendingCount }}</b> 个没回填微信道具 ID
          </span>
          <span v-else-if="soldCount > 0">在售档位已全部配好，可以开启虚拟支付</span>
          <span v-else>当前没有在售的档位，用户端充值页会显示为空</span>
        </div>
        <p class="pkg-progress__desc">
          <template v-if="pendingCount > 0">
            在 MP 后台【虚拟支付 → 道具管理】建好道具后，把「道具 ID」回填到这里再上架。
            没回填的档位不会出现在虚拟支付通道下（用户看不到，也就不会点到报错）。
          </template>
          <template v-else-if="soldCount === 0">
            至少上架一个档位，用户才能充值。新增后记得回填道具 ID。
          </template>
          <template v-else>
            别忘了：微信侧的道具价格必须等于这里的售价 × 100（分），否则用户下单会失败。
          </template>
        </p>
      </div>
      <div class="pkg-progress__bar">
        <i :style="{ width: readyPercent + '%' }" />
      </div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column label="售价" min-width="160">
          <template slot-scope="{ row }">
            <div class="bean-cell"><b>{{ toBean(row.price) }}</b> 爱豆</div>
            <div class="cell-sub">¥{{ Number(row.price || 0).toFixed(2) }}</div>
          </template>
        </el-table-column>

        <el-table-column label="微信道具 ID" min-width="220">
          <template slot-scope="{ row }">
            <span v-if="row.productIdReady" class="tag is-mono">{{ row.productId }}</span>
            <span v-else class="pkg-missing">
              <i class="el-icon-warning-outline" /> 未回填
            </span>
          </template>
        </el-table-column>

        <el-table-column label="角标" width="100">
          <template slot-scope="{ row }">
            <span v-if="row.recommended === 1" class="tag is-coral">常用</span>
            <span v-else class="cell-sub">—</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="120">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.status === 1 ? 's1' : 's0'">
              <i />{{ row.status === 1 ? '在售' : '停售' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="更新时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.updatedAt) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click="handleEdit(row)">编辑</button>

              <!--
                上架按钮在缺道具 ID 时**直接禁用**并说明原因。
                后端也会拒（这是硬约束），但让运营在看到按钮的瞬间就知道
                "点不动是因为少填了东西"，比点一下再弹报错友好得多。
              -->
              <el-tooltip
                v-if="row.status !== 1"
                :disabled="row.productIdReady"
                content="上架前必须填写微信道具 ID"
                placement="top"
              >
                <span>
                  <button
                    type="button"
                    class="act act-plain"
                    :disabled="!row.productIdReady"
                    @click="handlePublish(row)"
                  >
                    上架
                  </button>
                </span>
              </el-tooltip>

              <el-dropdown v-else trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more">
                  更多<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="disable">
                    <i class="el-icon-remove-outline" /> 停售
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 停售后才出现删除入口：在售的一删，用户端档位瞬间消失，多一步是刻意的阻力 -->
      <div v-if="disabledList.length > 0" class="pkg-trash">
        <div class="pkg-trash__t">已停售的档位</div>
        <div class="pkg-trash__row">
          <span v-for="row in disabledList" :key="row.id" class="pkg-trash__item">
            ¥{{ Number(row.price).toFixed(2) }}
            <button type="button" class="act act-danger" @click="handleDelete(row)">删除</button>
          </span>
        </div>
      </div>
    </div>

    <!-- ── 新增 / 编辑 ───────────────────────────────────── -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="售价" prop="price">
          <el-input-number
            v-model="form.price"
            :min="0.01"
            :max="10000"
            :precision="2"
            :step="1"
            style="width: 100%;"
          />
          <div class="form-hint">
            用户到账 <b>{{ toBean(form.price) }}</b> 爱豆
            <template v-if="form.productId"> · 已填道具 ID</template>
            <template v-else> · 道具 ID 待回填</template>
          </div>
        </el-form-item>

        <el-form-item label="微信道具 ID">
          <el-input v-model="form.productId" placeholder="可先留空，在 MP 后台建好道具后回来填" clearable />
          <div class="form-hint">
            在 MP 后台【虚拟支付 → 道具管理】新建道具后，把道具 ID 填到这里。
            <br>
            ⛔ 道具 ID 只允许<b>英文字母、数字、下划线</b>，发布后不可修改；上架前必填。
          </div>
        </el-form-item>

        <el-form-item label="标记常用">
          <el-switch
            v-model="form.recommended"
            :active-value="1"
            :inactive-value="0"
          />
          <div class="form-hint">打开后，用户端充值页会给这一档挂一个「常用」角标</div>
        </el-form-item>

        <el-alert
          v-if="priceChanged && form.status === 1"
          type="warning"
          :closable="false"
          show-icon
          title="这个档位在售中，改价后请同步修改 MP 后台的道具价格"
          description="微信侧道具价必须等于这里的售价 × 100（分），两边不一致用户下单会直接失败。"
          style="margin-bottom: 4px;"
        />
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getBeanPackageList,
  createBeanPackage,
  updateBeanPackage,
  deleteBeanPackage,
  publishBeanPackage,
  disableBeanPackage
} from '@/api/recharge'
import { toBean } from '@/utils/bean'

/**
 * 爱豆套餐（充值档位）
 * ---------------------------------------------------------------------------
 * 这一页对应后端 bean_packages 表，决定用户端「获取爱豆」页能买到哪些档位。
 *
 * 两个必须理解的规则：
 *   ① 售价存「元」，用户看到的是爱豆（按 bean.per_yuan 现算），所以改换算率
 *      会同时影响所有档位的豆数 —— 这是刻意的，不是 bug。
 *   ② 「在售」与「有道具 ID」是两件事：
 *      在售 = 该出现在充值页；道具 ID = 虚拟支付下能不能真的下单。
 *      虚拟支付通道只显示「在售 且 有道具 ID」的档位；旧微信支付通道只要求「在售」。
 */
export default {
  name: 'RechargePackage',
  data() {
    return {
      list: [],
      listLoading: false,
      dialogVisible: false,
      dialogTitle: '新增套餐',
      /** 编辑前的原价，用来判断"改了价且在售" → 提示同步微信侧道具价 */
      originalPrice: null,
      form: {
        id: null,
        price: 10,
        productId: '',
        status: 0,
        recommended: 0
      },
      rules: {
        price: [
          { required: true, message: '请输入售价', trigger: 'blur' },
          { type: 'number', min: 0.01, message: '售价不能小于 0.01 元', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    /** 在售档位 */
    soldList() {
      return this.list.filter(row => row.status === 1)
    },
    /** 停售档位（可以删的那批） */
    disabledList() {
      return this.list.filter(row => row.status !== 1)
    },
    soldCount() {
      return this.soldList.length
    },
    /** 在售但没回填道具 ID 的数量 —— 这是这一页最需要被解决的数字 */
    pendingCount() {
      return this.soldList.filter(row => !row.productIdReady).length
    },
    readyPercent() {
      if (this.soldCount === 0) return 0
      return Math.round((this.soldCount - this.pendingCount) / this.soldCount * 100)
    },
    /**
     * 是否处于"在售 + 改了价"的状态。
     * 只有这种情况才需要提醒去 MP 后台同步道具价 —— 新增（还没上架）时谈不上影响。
     */
    priceChanged() {
      if (!this.form.id || this.form.status !== 1 || this.originalPrice === null) {
        return false
      }
      return Number(this.form.price) !== Number(this.originalPrice)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    toBean(yuan) {
      return toBean(yuan)
    },
    fetchData() {
      this.listLoading = true
      getBeanPackageList().then(res => {
        this.list = res.data || []
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },
    handleAction(cmd, row) {
      if (cmd === 'disable') return this.handleDisable(row)
    },
    handleCreate() {
      this.dialogTitle = '新增套餐'
      this.originalPrice = null
      this.form = {
        id: null,
        price: 10,
        productId: '',
        status: 0,
        recommended: 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    handleEdit(row) {
      this.dialogTitle = '编辑套餐'
      this.originalPrice = Number(row.price)
      this.form = {
        id: row.id,
        price: Number(row.price),
        productId: row.productId || '',
        status: row.status,
        recommended: row.recommended || 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return

        // ⛔ 不改状态：上架/停售只走专门的按钮，这样"上架必须已回填道具 ID"
        //    这条规则只有一个入口，不会被编辑表单绕过去。
        const payload = {
          price: Number(this.form.price),
          productId: this.form.productId || '',
          status: this.form.status,
          recommended: this.form.recommended
        }

        if (this.form.id) {
          updateBeanPackage(this.form.id, payload).then(() => {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.fetchData()
          })
        } else {
          createBeanPackage(payload).then(() => {
            this.$message.success('创建成功，别忘了回填道具 ID 再上架')
            this.dialogVisible = false
            this.fetchData()
          })
        }
      })
    },
    handlePublish(row) {
      if (!row.productIdReady) {
        this.$message.warning('上架前必须填写微信道具 ID')
        return
      }
      this.$confirm(
        `上架「¥${Number(row.price).toFixed(2)} / ${toBean(row.price)} 爱豆」？上架后用户端立刻可见。`,
        '上架套餐',
        { confirmButtonText: '上架', cancelButtonText: '取消', type: 'warning' }
      ).then(() => {
        publishBeanPackage(row.id).then(() => {
          this.$message.success('已上架')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handleDisable(row) {
      this.$confirm(
        `停售「¥${Number(row.price).toFixed(2)}」？停售后用户端不再显示该档位（已产生的订单不受影响）。`,
        '停售套餐',
        { confirmButtonText: '停售', cancelButtonText: '取消', type: 'warning' }
      ).then(() => {
        disableBeanPackage(row.id).then(() => {
          this.$message.success('已停售')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(
        `删除「¥${Number(row.price).toFixed(2)}」这个档位？删除后不可恢复。`,
        '删除套餐',
        { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
      ).then(() => {
        deleteBeanPackage(row.id).then(() => {
          this.$message.success('已删除')
          this.fetchData()
        })
      }).catch(() => {})
    },
    formatDate(date) {
      if (!date) return '—'
      return date
    }
  }
}
</script>

<style lang="scss" scoped>
/* ── 配置进度条：本页的视觉重心 ────────────────────────── */
.pkg-progress {
  background: var(--warn-bg);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;

  &.is-done {
    background: var(--ok-bg);
  }
}

.pkg-progress__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--warn-text);

  b {
    font-size: 15px;
    font-weight: 600;
  }

  .is-done & {
    color: var(--ok-text);
  }
}

.pkg-progress__desc {
  margin: 6px 0 0 22px;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--ink-500);
}

.pkg-progress__bar {
  margin: 10px 0 0 22px;
  height: 4px;
  max-width: 320px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.07);
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    border-radius: 2px;
    background: var(--ok);
    transition: width 0.3s;
  }
}

/* ── 未回填道具 ID 的显式提示 ──────────────────────────── */
.pkg-missing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  color: var(--danger);
}

/* 禁用的「上架」按钮：默认 .act 没有禁用态，不处理的话点不动却看不出原因 */
.row-actions .act[disabled] {
  color: var(--ink-300);
  cursor: not-allowed;
  background: transparent;
}

/* ── 停售区（删除入口） ────────────────────────────────── */
.pkg-trash {
  border-top: 1px solid var(--ink-100);
  padding: 14px 20px 4px;
}

.pkg-trash__t {
  font-size: 12px;
  color: var(--ink-400);
  letter-spacing: 0.4px;
  margin-bottom: 10px;
}

.pkg-trash__row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

.pkg-trash__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink-700);
  font-variant-numeric: tabular-nums;
}

.bean-cell {
  font-size: 13px;
  color: var(--ink-900);
}

.bean-cell b {
  font-size: 15px;
  font-weight: 600;
  color: var(--rose-600);
}

.form-hint {
  font-size: 12px;
  color: var(--ink-400);
  line-height: 1.7;
  margin-top: 4px;

  b {
    color: var(--rose-600);
    font-weight: 600;
  }
}
</style>
